/**
 * floorFit — mappe la géométrie d'étage (mètres, repère centré sur l'emprise du lot) vers les coordonnées
 * normalisées du plan du lot. Espace « iso » : X = nx, Y = ny·ratio (distances isotropes).
 * Si le contour du lot a le même nombre de sommets que l'emprise (import), on ajuste une similitude
 * (échelle + rotation + translation) par moindres carrés → suit les déplacements/rotations du contour.
 */
const cen = (pts) => pts.reduce((a, p) => [a[0] + p[0] / pts.length, a[1] + p[1] / pts.length], [0, 0]);

export const buildFloorFit = ({ pointsM, outerPolygon, scale, ratio = 1 }) => {
  if (!pointsM?.length || !outerPolygon || outerPolygon.length < 3 || !scale) return null;
  const q = outerPolygon.map((p) => [p[0], p[1] * ratio]);
  const qc = cen(q);
  let z = [1 / scale, 0];
  let pc = [0, 0];
  if (pointsM.length === outerPolygon.length) {
    pc = cen(pointsM);
    let num = [0, 0]; let den = 0;
    pointsM.forEach((p, i) => {
      const a = [p[0] - pc[0], p[1] - pc[1]]; const b = [q[i][0] - qc[0], q[i][1] - qc[1]];
      num = [num[0] + a[0] * b[0] + a[1] * b[1], num[1] + a[0] * b[1] - a[1] * b[0]];
      den += a[0] * a[0] + a[1] * a[1];
    });
    if (den > 1e-9) z = [num[0] / den, num[1] / den];
  }
  const t = [qc[0] - (z[0] * pc[0] - z[1] * pc[1]), qc[1] - (z[0] * pc[1] + z[1] * pc[0])];
  const toNorm = (m) => [z[0] * m[0] - z[1] * m[1] + t[0], (z[0] * m[1] + z[1] * m[0] + t[1]) / ratio];
  return { toNorm, scaleNormPerM: Math.hypot(z[0], z[1]), angleDeg: (Math.atan2(z[1], z[0]) * 180) / Math.PI };
};

/** Rotation d'un polygone normalisé autour de son centroïde (angle en degrés, ratio = H/W). */
export const rotatePolygon = (poly, deg, ratio = 1) => {
  const c = cen(poly); const a = (deg * Math.PI) / 180; const cs = Math.cos(a); const sn = Math.sin(a);
  return poly.map((p) => {
    const x = p[0] - c[0]; const y = (p[1] - c[1]) * ratio;
    return [Math.min(1, Math.max(0, c[0] + x * cs - y * sn)), Math.min(1, Math.max(0, c[1] + (x * sn + y * cs) / ratio))];
  });
};

/**
 * Similitude définie par 2 paires (source → cible) en coordonnées normalisées : retourne une fonction point → point
 * (clampée) avec `scale` et `angleDeg`, ou null si les sources sont confondues.
 */
export const similarityFromPairs = (s1, c1, s2, c2, ratio = 1) => {
  const iso = (p) => [p[0], p[1] * ratio];
  const [a1, b1, a2, b2] = [iso(s1), iso(c1), iso(s2), iso(c2)];
  const ds = [a2[0] - a1[0], a2[1] - a1[1]]; const dc = [b2[0] - b1[0], b2[1] - b1[1]];
  const den = ds[0] * ds[0] + ds[1] * ds[1];
  if (den < 1e-10) return null;
  const z = [(dc[0] * ds[0] + dc[1] * ds[1]) / den, (dc[1] * ds[0] - dc[0] * ds[1]) / den];
  const t = [b1[0] - (z[0] * a1[0] - z[1] * a1[1]), b1[1] - (z[0] * a1[1] + z[1] * a1[0])];
  const f = (p) => { const q = iso(p); const x = z[0] * q[0] - z[1] * q[1] + t[0]; const y = (z[0] * q[1] + z[1] * q[0] + t[1]) / ratio; return [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))]; };
  f.scale = Math.hypot(z[0], z[1]); f.angleDeg = (Math.atan2(z[1], z[0]) * 180) / Math.PI;
  return f;
};
